/**
 * Web-push configuration shared by the client (subscribe) and the backend
 * (send, Phase 2).
 *
 * The VAPID *public* key is safe to ship to the browser — it is the
 * `applicationServerKey` every push subscription is signed against, and is
 * meant to be public. The matching *private* key is a backend-only secret
 * (`VAPID_PRIVATE_KEY`, set via `wrangler secret put` on the backend worker)
 * and is never imported here or bundled into any client/worker graph.
 *
 * Keypair generated 2026-07-01. Rotating it invalidates every existing
 * subscription (browsers must re-subscribe), so treat it as stable.
 */
export const VAPID_PUBLIC_KEY =
	"BB2vy5RsKHMIXzXOqRUtBDbMvT56vVEZOMD-rCQAKoYihhr00Q-Yow_ZCITYbajEFORILmtNnf86rrMjoPYGdjw";

/** `sub` claim for the VAPID JWT the backend signs at send time. */
export const VAPID_SUBJECT = "mailto:kelvin@lokerdollar.com";

/** PostHog flag that gates the whole web-push surface (client prompt + send). */
export const WEB_PUSH_FLAG = "web-push";

/**
 * PostHog flag that gates the "new blog post" broadcast push (Wave 3 PR-3c).
 * Ships dark (default false, no PostHog flag created yet) — a dedicated
 * switch separate from WEB_PUSH_FLAG so this specific broadcast can be tuned
 * (audience cap, cadence) independently before it's ever turned on. Both
 * flags must be on for a send to actually go out (see push/deliver.ts).
 */
export const BLOG_PUBLISH_PUSH_FLAG = "blog-publish-push";

/**
 * Convert a base64url-encoded VAPID key into the `Uint8Array` that
 * `pushManager.subscribe({ applicationServerKey })` requires.
 *
 * Explicitly backed by a fresh `ArrayBuffer` (not the generic `ArrayBufferLike`,
 * which TS 5.7 widens to include `SharedArrayBuffer`) so the result satisfies
 * `BufferSource` without a cast.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
	const padding = "=".repeat((4 - (base64.length % 4)) % 4);
	const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(normalized);
	const buffer = new ArrayBuffer(raw.length);
	const output = new Uint8Array(buffer);
	for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
	return output;
}
